import React from 'react';
import { Link } from '@/components/LocaleLink';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { caseStudyAltisportCopy } from '@/contexts/language/translations/pages/caseStudyAltisport';
import { caseStudyChromeCopy } from '@/contexts/language/translations/pages/caseStudyChrome';

// Copy (EN + SK) lives in src/contexts/language/translations/pages/caseStudyAltisport.ts
const TECH_STACK = ['Vario', 'Google Analytics 4', 'Google Ads', 'Meta Ads', 'Bing Ads', 'Google BigQuery', 'dbt', 'Klaviyo', 'Claude AI'];

const CaseStudyAltisport = () => {
  useScrollReveal();
  const { language } = useLanguage();
  const c = caseStudyAltisportCopy[language];
  const ui = caseStudyChromeCopy[language];
  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={c.seoTitle}
        description={c.seoDescription}
        canonical="/case-studies/altisport"
        ogType="article"
      />
      <Navigation />
      <main className="flex-1">

        {/* Hero */}
        <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-[#2A1E47]">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FF6B35]/10 rounded-full filter blur-3xl" />
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto reveal">
              <div className="flex items-center gap-2 text-white/40 text-sm mb-8">
                <Link to="/content" className="hover:text-white/70 transition-colors">{ui.breadcrumbContent}</Link>
                <span>/</span>
                <span className="text-white/60">{ui.caseStudy}</span>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <span className="text-xs font-bold uppercase tracking-widest bg-[#FF6B35] text-white px-3 py-1 rounded-full">
                  {ui.caseStudy}
                </span>
                <span className="text-xs text-white/40 uppercase tracking-widest font-semibold">
                  {c.category}
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.08]">
                {c.title}
              </h1>

              <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-3xl">
                {c.lead}
              </p>

              <div className="flex flex-wrap items-center gap-4 text-sm text-white/50 mt-8">
                <span className="font-semibold text-white/80">Altisport</span>
                <span>·</span>
                <span>{c.industry}</span>
                <span>·</span>
                <span>{ui.cooperationSince('2026')}</span>
                <span>·</span>
                <span>{ui.ongoing}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Stats bar */}
        <section className="bg-[#FF6B35] py-10">
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-5xl mx-auto">
              {c.stats.map(stat => (
                <div key={stat.value} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</p>
                  <p className="text-xs text-white/80 font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto space-y-16 reveal">

              {/* The Challenge */}
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-3">{c.challenge.eyebrow}</p>
                <h2 className="text-2xl md:text-3xl font-bold text-[#2A1E47] mb-5">
                  {c.challenge.title}
                </h2>
                <p className="text-[#2A1E47]/70 leading-relaxed text-lg mb-4">
                  {c.challenge.p1}
                </p>
                <p className="text-[#2A1E47]/70 leading-relaxed text-lg">
                  {c.challenge.p2}
                </p>
                <blockquote className="border-l-4 border-[#FF6B35] pl-6 py-2 my-8">
                  <p className="text-xl italic text-[#2A1E47]/80 leading-relaxed">
                    {c.challenge.quote}
                  </p>
                </blockquote>
              </div>

              {/* The Solution */}
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-3">{c.solution.eyebrow}</p>
                <h2 className="text-2xl md:text-3xl font-bold text-[#2A1E47] mb-5">
                  {c.solution.title}
                </h2>
                <p className="text-[#2A1E47]/70 leading-relaxed text-lg">
                  {c.solution.p1}
                </p>
              </div>

              {/* What was built */}
              <div className="bg-[#FAFAFA] rounded-2xl p-8 md:p-10">
                <h2 className="text-xl font-bold text-[#2A1E47] mb-6">{ui.whatWasBuilt}</h2>
                <div className="space-y-4">
                  {c.whatWasBuilt.map((step, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-6 h-6 rounded-full bg-[#2A1E47] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                        {i + 1}
                      </div>
                      <p className="text-[#2A1E47]/80 leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2A1E47]/40 mb-3">{ui.techStack}</p>
                  <div className="flex flex-wrap gap-2">
                    {TECH_STACK.map(tech => (
                      <span key={tech} className="text-xs font-medium text-[#2A1E47]/70 bg-white border border-gray-200 px-3 py-1 rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* The breakthrough */}
              <div className="bg-[#2A1E47] rounded-2xl p-8 md:p-10 text-white">
                <h2 className="text-xl md:text-2xl font-bold mb-4">{c.breakthrough.title}</h2>
                <p className="text-white/70 leading-relaxed text-lg mb-4">
                  {c.breakthrough.p1}
                </p>
                <p className="text-white/70 leading-relaxed text-lg">
                  {c.breakthrough.p2}
                </p>
              </div>

              {/* The Results */}
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-3">{c.results.eyebrow}</p>
                <h2 className="text-2xl md:text-3xl font-bold text-[#2A1E47] mb-5">
                  {c.results.title}
                </h2>
                <p className="text-[#2A1E47]/70 leading-relaxed text-lg mb-8">
                  {c.results.p1}
                </p>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2A1E47]/40 mb-4">{ui.whatChanged}</p>
                <ul className="space-y-3">
                  {c.whatChanged.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B35] shrink-0 mt-2.5" />
                      <span className="text-[#2A1E47]/80 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Testimonial */}
              <figure className="bg-[#FFF7EC] rounded-2xl p-8 md:p-10 border-l-4 border-[#FF6B35]">
                <blockquote>
                  <p className="text-lg md:text-xl italic text-[#2A1E47]/80 leading-relaxed">
                    {c.testimonial.quote}
                  </p>
                </blockquote>
                <figcaption className="mt-6">
                  {/* Funkcia COO podľa LinkedInu (6. 10. 2026) */}
                  <p className="font-bold text-[#2A1E47]">Vojtěch Titlbach</p>
                  <p className="text-sm text-[#2A1E47]/60">{c.testimonial.role}</p>
                </figcaption>
              </figure>

              {/* Why it matters */}
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-3">{c.whyItMatters.eyebrow}</p>
                <h2 className="text-2xl md:text-3xl font-bold text-[#2A1E47] mb-5">
                  {c.whyItMatters.title}
                </h2>
                <p className="text-[#2A1E47]/70 leading-relaxed text-lg mb-5">
                  {c.whyItMatters.p1}
                </p>
                <p className="text-[#2A1E47]/70 leading-relaxed text-lg">
                  {c.whyItMatters.p2}
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* CTA / Back */}
        <section className="py-16 bg-[#FAFAFA]">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center mb-8">
              <p className="text-xl md:text-2xl font-bold text-[#2A1E47]">
                {ui.closingLine}
              </p>
            </div>
            <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
              <Link
                to="/content"
                className="flex items-center gap-2 text-sm font-semibold text-[#2A1E47]/60 hover:text-[#2A1E47] transition-colors"
              >
                {ui.backToContent}
              </Link>
              <Link
                to="/tell-me-more"
                className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors"
              >
                {ui.ctaSimilarProject}
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default CaseStudyAltisport;
